/**
 * Org Settings → Workspaces. Lists every active (non-soft-deleted)
 * workspace under the org with role-gated `+ New workspace` and
 * inline soft-delete affordances (#547 PR 3).
 *
 * Why "inline soft-delete" instead of pushing through each
 * workspace's own Danger zone:
 *   The org's Workspaces tab is the natural surface for "clean up
 *   old workspaces" — an org admin scanning a list of 20 workspaces
 *   needs the action one click away, not gated behind nine extra
 *   navigations. The confirmation dialog + the 30-day recovery
 *   window (Recovery tab) keep the guardrail tight; the cost of an
 *   accidental delete is a single "Restore" click.
 *
 * Soft-deleted workspaces appear in the sibling Recovery tab — they
 * don't show here.
 *
 * Gates:
 *   - List: any org member (we just read the list of workspaces the
 *     caller can reach, filtered by `orgId === org._id`; the server
 *     enforces visibility per-workspace).
 *   - + New workspace: `workspaces.create` (org-tier) — owner /
 *     admin / member (GitHub-style; matches the picker's `+ New
 *     workspace` affordance).
 *   - Soft-delete: `workspaces.delete` (workspace-tier, per-row) —
 *     a workspace Admin or an org Owner (implicit Admin on every
 *     workspace) can delete. Members of the org who happen to
 *     have only Reviewer/Tester grants on a specific workspace
 *     do NOT see the trash icon for THAT workspace — even if they
 *     can create workspaces (org-tier). The per-row check uses
 *     `useCanIWorkspace` against each row's id so the affordance
 *     never shows a button the backend would 403 on click.
 *     PR #558 Copilot review.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Settings as SettingsIcon, Trash2 } from 'lucide-react'
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui'
import {
  workspacesAPI,
  type OrgWithRole,
  type WorkspaceSummary,
} from '@/lib/api'
import { toUserMessage } from '@/lib/userMessage'
import { useCanI, useCanIWorkspace } from '@/hooks/useCanI'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { formatDate } from '@/lib/utils'

export function OrgWorkspaces({ org }: { org: OrgWithRole }) {
  const canCreate = useCanI('workspaces.create', org._id)

  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<WorkspaceSummary | null>(null)

  const refresh = useCallback(async () => {
    try {
      setLoadError(null)
      const all = await workspacesAPI.list()
      // The server's list returns every workspace the caller can
      // reach across all their orgs. Filter to the active org since
      // this tab is org-scoped.
      setWorkspaces(
        all
          .filter((w) => w.orgId === org._id)
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
      )
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err))
    }
  }, [org._id])

  useEffect(() => {
    void refresh()
  }, [refresh])

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive" role="alert">
          Failed to load workspaces: {loadError}
        </p>
        <Button variant="outline" size="sm" onClick={() => void refresh()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Workspaces</h3>
          <p className="text-sm text-muted-foreground">
            Workspaces under <strong>{org.name}</strong>. Deleting moves a
            workspace into a 30-day recovery window — see the Recovery tab.
          </p>
        </div>
        {canCreate && (
          <Button variant="outline" size="sm" onClick={() => setShowCreate((s) => !s)}>
            {showCreate ? 'Cancel' : '+ New workspace'}
          </Button>
        )}
      </div>

      {showCreate && canCreate && (
        <CreateWorkspaceForm
          orgId={org._id}
          onCancel={() => setShowCreate(false)}
          onCreated={async () => {
            setShowCreate(false)
            await refresh()
          }}
        />
      )}

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {workspaces.map((ws) => (
              <WorkspaceRow
                key={ws._id}
                workspace={ws}
                onRequestDelete={() => setConfirmDelete(ws)}
              />
            ))}
            {workspaces.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="text-center text-sm text-muted-foreground py-8">
                  No workspaces yet in {org.name}.
                  {canCreate && ' Click "+ New workspace" to create one.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <DeleteWorkspaceDialog
        workspace={confirmDelete}
        onCancel={() => setConfirmDelete(null)}
        onConfirmed={async () => {
          setConfirmDelete(null)
          await refresh()
        }}
      />
    </div>
  )
}

// ── Workspace row (per-row component so useCanIWorkspace can gate
// the trash icon against THIS row's id — the org-level "canCreate"
// flag isn't a substitute, because an org member who can create
// workspaces may still have only Reviewer access to a specific
// existing workspace and should not see a delete affordance for it.
// PR #558 Copilot review.) ──────────────────────────────────────────────

function WorkspaceRow({
  workspace,
  onRequestDelete,
}: {
  workspace: WorkspaceSummary
  onRequestDelete: () => void
}) {
  const canDelete = useCanIWorkspace('workspaces.delete', workspace._id)
  const tz = useUserTimezone()
  return (
    <TableRow>
      <TableCell className="font-medium">{workspace.name}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {formatDate(workspace.createdAt, tz)}
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            asChild
            title={`Open ${workspace.name} settings`}
          >
            {/* Land on the workspace's General tab — without an
                explicit `?tab=`, the Settings shell defaults to
                Account → Profile and the click lands on the wrong
                surface. PR #558 Copilot review. */}
            <Link
              to={`/workspaces/${workspace._id}/settings?tab=workspace.general`}
              aria-label={`Open ${workspace.name} settings`}
            >
              <SettingsIcon className="h-4 w-4" />
            </Link>
          </Button>
          {canDelete && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onRequestDelete}
              title={`Delete ${workspace.name}`}
              aria-label={`Delete ${workspace.name}`}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  )
}

// ── Create workspace form (inline below header) ────────────────────────

function CreateWorkspaceForm({
  orgId,
  onCancel,
  onCreated,
}: {
  orgId: string
  onCancel: () => void
  onCreated: () => void
}) {
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    setSubmitting(true)
    setError(null)
    try {
      await workspacesAPI.createInOrg(orgId, trimmed)
      onCreated()
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't create the workspace. Try again — if the problem continues, check your connection.",
        ),
      )
      setSubmitting(false)
    }
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="new-ws-name">Workspace name</Label>
            <Input
              id="new-ws-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Marketing"
              autoFocus
              disabled={submitting}
              required
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!name.trim() || submitting}>
              {submitting ? 'Creating…' : 'Create workspace'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}

// ── Delete-confirmation dialog ─────────────────────────────────────────

function DeleteWorkspaceDialog({
  workspace,
  onCancel,
  onConfirmed,
}: {
  workspace: WorkspaceSummary | null
  onCancel: () => void
  onConfirmed: () => void
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const open = useMemo(() => workspace !== null, [workspace])

  useEffect(() => {
    // Reset error state every time the dialog opens for a new
    // workspace so a prior failure doesn't haunt the next attempt.
    if (open) {
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  async function handleDelete() {
    if (!workspace) return
    setSubmitting(true)
    setError(null)
    try {
      await workspacesAPI.softDelete(workspace._id)
      onConfirmed()
    } catch (err) {
      setError(
        toUserMessage(
          err,
          "Couldn't delete the workspace. Try again. If it keeps failing, ask whoever runs OttoTester here to check the server log.",
        ),
      )
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete workspace?</DialogTitle>
          <DialogDescription>
            <strong>{workspace?.name ?? ''}</strong> will be moved into the
            30-day recovery window. Members lose access immediately. You can
            restore it from the Recovery tab any time before the window
            closes; after that, all data is permanently removed.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleDelete} disabled={submitting}>
            {submitting ? 'Deleting…' : 'Delete workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
